import type { AnnounceOptions, AnnouncedLine, AudioEngine, CrowdReaction } from '@hf/audio';
import { effect } from '@preact/signals-core';
import { CHARACTER_IDS, MatchFormat, RULES, yardsToGoal } from '@hf/shared';
import {
  BallState,
  PlayResultKind,
  PlayerState,
  Phase,
  eventKey,
  type SimEvent,
  type SimState,
} from '@hf/sim';
import { pendingMatch } from '../ui-model/flow';
import type { UiStore } from '../ui-model/store';
import type { ScreenId } from '../ui-model/types';
import { crowdLevel, panFor, type CrowdMode } from './crowd-level';
import {
  LINE_VARIANTS,
  LONG_TD_TAKES,
  LONG_TD_YARDS,
  READY_LINES,
  STINGER_RANK,
  characterLine,
  clearBatch,
  createBatch,
  cuesFor,
  type LineCue,
} from './cues';
import type { AudioBridge, TheaterTape } from './types';

export * from './types';
export { loadAudioManifest } from './manifest';

/** Ticks after which an event counts as stale (late rollback delivery / long frame): movement noise is skipped. */
export const STALE_TICKS = 20;
/** Remembered event keys (de-duplication across rollback resimulation). */
const DEDUPE_CAPACITY = 1024;
/** Min gap between two colour-commentary lines (ms): from the end of the last one. */
const CHATTER_GAP_MS = 2500;
/** A colour line that never reported its end (dropped from the queue) stops blocking colour after this (ms). */
const CHATTER_BUSY_MAX_MS = 7000;
/**
 * Heat needed for "HE'S ON FIRE!!". Heat comes from scores and takeaways only (a touchdown or a pick is 2, a field
 * goal 1) and the opponent scoring or taking the ball away cools it: three unanswered touchdowns.
 */
export const ON_FIRE_HEAT = 6;
/** Heat at which the streak is first noticed ("Ohh, he's heating up!"): the second unanswered score. */
export const HEATING_UP_HEAT = 3;
/** Streak calls hold this long (ms) before they are asked for: a walk-off score's final whistle cancels them. */
export const STREAK_HOLD_MS = 600;
/** How long the booth narrating an instant replay fades out when the tape is skipped / closed (ms). */
export const REPLAY_SKIP_FADE_MS = 200;
/**
 * A tape that ran to its end wipes back to live over ~0.8 s: narration with at most this much left (ms) lands
 * ("…TOUCHDOWN!!!" over the wipe) instead of being faded on its last word. A skip mid-tape gets the booth's
 * usual last-word grace only.
 */
export const REPLAY_END_GRACE_MS = 1300;
/** The booth's replay narration lines (stopped when the tape closes: never over live play). */
const REPLAY_LINES: readonly string[] = [...LONG_TD_TAKES, 'replay', 'replay_2'];
/**
 * The long-score narration ("He's at the forty… the twenty… TOUCHDOWN!!!", 5-7 s) is a set piece: at most this often
 * in a match (ms). Every other replay of a long score gets the short lead-in (one take on every tape was a loop).
 */
export const LONG_TD_COOLDOWN_MS = 120_000;
/** …and on the Results reel, where the long scores' tapes run back to back (ms). */
export const LONG_TD_REEL_COOLDOWN_MS = 20_000;
/**
 * A highlight call (WHAT A CATCH!!, a fake, a hurdle…) made while the carrier is this close to the goal (yards) is
 * held: a touchdown a moment later would cut it on its first word ("WHAT A CA— TOUCHDOWN!!"). The touchdown call
 * replaces it; a run stopped short (or an incompletion) lets it go at once. A pass in flight at the goal holds it
 * too (a scrambling carrier flicks it into the end zone half a second after the call).
 */
export const HIGHLIGHT_HOLD_YARDS = 15;
/** …or, at speed, this many seconds of running from it (a dash covers 15 yards in about a second). */
export const HIGHLIGHT_HOLD_LOOKAHEAD_S = 2;
/** A held highlight still waiting this long (ms) is stale (the run goes on at the goal line): dropped. */
export const HIGHLIGHT_HOLD_MAX_MS = 2200;
/** The calls a scoring run holds (mid-play highlights; results and specials are never held). */
const HOLDABLE: ReadonlySet<string> = new Set([
  'what_a_catch',
  'what_a_fake',
  'right_through_him',
  'over_the_top',
  'spin_move',
  'no_way',
]);
/**
 * A dead-ball line (the special-ready callout) starts in the line-up only this early in it (frames): the snap may come 30 frames in, and a 4.5 s read that
 * starts late runs into the play (a pick-up fake or the special itself cut it on its first words).
 */
export const DEAD_BALL_PRESNAP_FRAMES = 8;
/** Play results that roll an instant replay: their dead ball belongs to the tape (dead-ball lines wait). */
const REPLAYED_RESULTS: ReadonlySet<number> = new Set<number>([
  PlayResultKind.TOUCHDOWN,
  PlayResultKind.PICK_SIX,
  PlayResultKind.INTERCEPTION,
  PlayResultKind.SAFETY,
]);
/** Fade (ms) of a Football 101 coach line still talking when a lesson hands off to a real game's Versus splash. */
const HANDOFF_FADE_MS = 120;
/** Classic: "Thirty seconds left in the half…" when the game clock crosses this (frames). */
export const CLOCK_WARNING_FRAMES = RULES.classicClockStopsLastFrames;
/** Fade (ms) for everything match-bound (announcer, reactions, gameplay voices) when leaving the stadium. */
const LEAVE_FADE_MS = 350;
/** Crowd bed level under the results screen (the stadium is still there behind the scoreboard). */
const RESULTS_CROWD = 0.25;
/** The Classic clock warnings: the first half's and the game's (second half). */
const isThirty = (id: string): boolean => id === 'thirty_seconds' || id === 'thirty_seconds_game';
/** Classic: how long "Thirty seconds left…" keeps trying when the booth is busy at the :30 crossing (ms). */
export const CLOCK_WARNING_RETRY_MS = 6000;
/** A clock warning cut within this long of its start (ms) was not heard: it is tried again. */
const THIRTY_RETRY_IF_CUT_MS = 1500;
/** Results replay theater: the victory outro sits this low under the tapes. */
const THEATER_MUSIC = 0.3;
/**
 * How long (s) a call (priority ≥ 2: a play's result, a highlight) may wait for the booth when a call of the same
 * weight is talking: "SACKED!!" behind "BLITZ!! Here comes Grandpa!" (2.3 s), "DENIED!!" behind "WHAT A CATCH!!"
 * are still news that late. Colour (priority 1) keeps the booth's 1.6 s: a stale bark is worse than silence.
 */
export const CALL_WAIT_S = 2.6;
/** Lines below this priority are dropped in a Football 101 lesson (the coach talks; the stadium calls big moments). */
const LESSON_MIN_PRIORITY = 2.5;

export interface AudioBridgeOptions {
  /** ms clock for announcer cooldowns (default performance.now). */
  now?: () => number;
  /** Randomness for colour-line chances (cosmetic). */
  random?: () => number;
}

type Music = 'mus_title' | 'mus_menu' | 'mus_results' | null;
const SCREEN_MUSIC: Record<ScreenId, Music | 'keep'> = {
  boot: 'keep',
  title: 'mus_title',
  main_menu: 'mus_menu',
  mode_select: 'mus_menu',
  character_select: 'mus_menu',
  store: 'mus_menu',
  settings: 'mus_menu',
  profile: 'mus_menu',
  online_lobby: 'mus_menu',
  versus: null,
  match: null,
  // Football 101's hub and the Practice setup are menus (lessons and drills run on the match screen)
  practice: 'mus_menu',
  tutorial: 'mus_menu',
  results: 'mus_results',
};
const IN_STADIUM: ReadonlySet<ScreenId> = new Set<ScreenId>(['versus', 'match']);
/** Screens with stadium sound (crowd, reactions, announcer) still running. */
const isLive = (s: ScreenId): boolean => IN_STADIUM.has(s) || s === 'results';

/**
 * The music for `next`. The title theme carries over to the main menu (audio unlocks on the title's "press any
 * key", the same gesture that opens the menu ~0.4 s later, so switching there would mean it is never heard); the
 * menu theme takes over one level deeper.
 */
const musicFor = (next: ScreenId, current: Music | undefined): Music | 'keep' => {
  if (next === 'main_menu' && current === 'mus_title') return 'keep';
  return SCREEN_MUSIC[next];
};

/**
 * THE audio bridge factory used by src/app (the audio team replaces only this body). `engine` is the app-wide
 * engine (createClientAudioEngine); `ui` is for announcer captions (`ui.pushCaption`), settings (volumes) and the
 * pause overlay. The SceneManager calls `onScreen` on every navigation; the match scene calls `onEvents`/`update`
 * every rendered frame.
 */
export const createAudioBridge = (
  engine: AudioEngine,
  ui: UiStore,
  opts: AudioBridgeOptions = {},
): AudioBridge => {
  const now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
  const random = opts.random ?? Math.random;

  let screen: ScreenId = 'boot';
  let disposed = false;
  let titleShouted = false;
  let lastStateTick = -1;
  let crowdSent = -1;
  let musicSent: Music | undefined;
  let chantSent = false;
  const seen = new Set<string>();
  const seenOrder: string[] = [];
  const batch = createBatch();
  /** When each call (a cue id: a variant group or a single line) was last said: its cooldown. */
  const groupLast = new Map<string, number>();
  /** When each TAKE was last heard: variant rotation (kept apart from the groups — a group id can be a take). */
  const takeLast = new Map<string, number>();
  /** When the last colour line finished (or started, while it is talking): the chatter gap runs from here. */
  let lastChatter = -Infinity;
  /** A colour line is talking (or queued) until this time (ms; cleared when it ends). */
  let chatterBusyUntil = -Infinity;
  /** Take ids said as colour, waiting for their end. */
  const chatterOpen = new Set<string>();
  const heat = [0, 0];
  /** "He's heating up!" was called in the current streak of each player. */
  const heatingCalled = [false, false];
  /** Streak calls already made per player this period (Classic: a half; Drives: regulation / overtime). */
  const heatingDone = [false, false];
  const fireDone = [false, false];
  /** A streak call waiting out `STREAK_HOLD_MS` (a game-ending score drops it). */
  let pendingStreak: { id: string; priority: number; at: number } | null = null;
  /** `once` lines already said this match. */
  const saidOnce = new Set<string>();
  /** Yards of the last touchdown play (the instant replay of a long one gets the long call). */
  let lastTdYards = 0;
  /** A highlight held while the carrier nears the goal (`HIGHLIGHT_HOLD_YARDS`). */
  let held: { cue: LineCue; since: number } | null = null;
  /** Lines waiting for a dead ball (`LineCue.deadBall`: the special-ready callouts). */
  const deadBallLines: LineCue[] = [];
  /** The last play ended in a result that rolls an instant replay (its dead ball is the tape's). */
  let replayedPlay = false;
  /** Replay clip id already called (one call per tape). */
  let calledReplay = -1;
  /** Classic game clock on the previous update (thirty-second warning crossing). */
  let prevClock = -1;
  let prevHalf = 0;
  /** Classic: "Thirty seconds left…" is pending until this time (ms; -1 = not pending). */
  let thirtyUntil = -1;
  /** When "Thirty seconds left…" last started (ms). */
  let thirtySaidAt = -Infinity;
  /** The match's first snap happened (the opener is never said after play started). */
  let snapped = false;
  /** Caption text of each line id the booth announced (removed from the HUD if the line is cut). */
  const captionOf = new Map<string, string>();
  /** Lines that finished speaking whose captions may still linger on the HUD (cleared when the next play starts). */
  const finished = new Set<string>();
  /** The Results replay theater is rolling a tape. */
  let theaterOn = false;
  const prevHop = [0, 0];
  const prevRun = [false, false];
  const disposers: (() => void)[] = [];
  let replayStage: string | null = null;

  /**
   * What the stadium is hosting: a match, a practice drill or a Football 101 lesson (all run on the match screen;
   * the 'practice' / 'tutorial' SCREENS are their menus).
   */
  const mode = (): CrowdMode => {
    if (screen !== 'match') return 'match';
    const m = ui.matchSetup.peek()?.mode;
    return m === 'tutorial' ? 'tutorial' : m === 'practice' ? 'practice' : 'match';
  };
  /** Football 101's Game Day: the real broadcast (chatter, full crowd). */
  const gameDay = (): boolean => ui.tutorial.peek()?.lessonId === 'scrimmage';
  /** The course is finished: the graduation owns the moment (no more calls, no boos). */
  const graduated = (): boolean => mode() === 'tutorial' && ui.tutorial.peek()?.status === 'complete';

  // ── captions: precise timing from the engine when it can tell us, else from announce()'s return value ────────
  const preciseCaptions = typeof engine.onAnnounce === 'function';
  if (preciseCaptions) {
    disposers.push(
      engine.onAnnounce!((l: AnnouncedLine) => {
        if (isThirty(l.id)) {
          thirtyUntil = -1;
          thirtySaidAt = now();
        }
        if (disposed || !l.caption) return;
        captionOf.set(l.id, l.caption);
        finished.delete(l.id);
        ui.pushCaption(l.caption, l.durationMs);
      }),
    );
  }
  // A line cut off by a bigger call takes its caption with it (the HUD shows what is being said).
  if (engine.onLineEnd) {
    disposers.push(
      engine.onLineEnd((id, cut) => {
        if (disposed) return;
        // the clock warning swapped out by a bigger call before it got going: say it again when the booth is free
        if (cut && isThirty(id) && now() - thirtySaidAt < THIRTY_RETRY_IF_CUT_MS)
          thirtyUntil = now() + CLOCK_WARNING_RETRY_MS / 2;
        if (chatterOpen.delete(id)) {
          lastChatter = now();
          chatterBusyUntil = -Infinity;
        }
        if (cut) dropCaption(id);
        else finished.add(id);
      }),
    );
  }
  /** Remove the HUD caption of a line (the newest one with its text). */
  function dropCaption(id: string): void {
    const text = captionOf.get(id);
    if (!text) return;
    const list = ui.captions.peek();
    let i = list.length - 1;
    while (i >= 0 && list[i]!.text !== text) i--;
    if (i >= 0) ui.captions.value = list.filter((_, k) => k !== i);
  }
  /** A new play starts: captions of lines that already finished are about the last play, take them down. */
  const clearFinishedCaptions = (): void => {
    for (const id of finished) dropCaption(id);
    finished.clear();
  };
  const say = (id: string, priority: number, chain = false, wait?: number): boolean => {
    const opts: AnnounceOptions = { priority };
    if (chain) opts.chain = true;
    else if (wait !== undefined) opts.wait = wait;
    const caption = engine.announce(id, opts);
    if (caption && !preciseCaptions) ui.pushCaption(caption, 2200);
    return caption !== null;
  };

  // ── announcer direction ─────────────────────────────────────────────────────────────────────────────────
  /** Takes of a call, least recently heard first (ties keep the listed order). */
  const takesOf = (id: string): readonly string[] => {
    const v = LINE_VARIANTS[id];
    if (!v) return [id];
    return [...v].sort((a, b) => (takeLast.get(a) ?? -Infinity) - (takeLast.get(b) ?? -Infinity));
  };

  /** Whether a cue may be said at all now (colour gap, once, cooldown, chance) — the booth aside. */
  const gate = (c: LineCue, t: number): boolean => {
    if (c.chatter) {
      if (mode() === 'tutorial' && !gameDay()) return false;
      // measured from the end of the last colour line (two 5 s reads 0.2 s apart sounded like one long ramble)
      if (t < chatterBusyUntil || t - lastChatter < CHATTER_GAP_MS) return false;
    }
    if (c.once && saidOnce.has(c.id)) return false;
    const last = groupLast.get(c.id);
    if (last !== undefined && t - last < (c.cooldownMs ?? 5000)) return false;
    if (c.chance !== undefined && c.chance < 1 && random() >= c.chance) return false;
    return true;
  };

  const tryLine = (c: LineCue): boolean => {
    const t = now();
    return gate(c, t) && speak(c, t);
  };

  /** Ask the booth for a cue that passed its gate (its takes, least recently heard first). */
  const speak = (c: LineCue, t: number): boolean => {
    // a take the manifest lacks (or the booth refuses) falls through to the next one
    let said: string | null = null;
    for (const take of takesOf(c.id)) {
      if (say(take, c.priority, c.chain, c.wait ?? (c.priority >= 2 ? CALL_WAIT_S : undefined))) {
        said = take;
        break;
      }
    }
    if (said === null) return false;
    groupLast.set(c.id, t);
    takeLast.set(said, t);
    if (c.once) saidOnce.add(c.id);
    if (c.chatter) {
      lastChatter = t;
      if (preciseCaptions && engine.onLineEnd) {
        // busy until it ends (onLineEnd); a line dropped from the queue unsaid never reports: give up after a while
        chatterOpen.add(said);
        chatterBusyUntil = t + CHATTER_BUSY_MAX_MS;
      }
    }
    return true;
  };

  /**
   * The booth calls an instant replay as the tape starts rolling: a long touchdown gets the full narration
   * ("He's at the forty… the twenty… he could go all the way… TOUCHDOWN!!!"), other tapes a short lead-in.
   * Chained, so the live call (TOUCHDOWN!!, HE'S ON FIRE!!) always finishes first.
   */
  const callReplay = (clipId: number, title: string): void => {
    if (disposed || screen !== 'match' || clipId === calledReplay) return;
    calledReplay = clipId;
    const long = /TOUCHDOWN/.test(title) && lastTdYards >= LONG_TD_YARDS;
    lastTdYards = 0;
    // the long narration at most every LONG_TD_COOLDOWN_MS (its takes rotate); otherwise the short lead-in
    if (long && tryLine({ id: 'long_td', priority: 2, cooldownMs: LONG_TD_COOLDOWN_MS, chain: true })) return;
    tryLine({ id: 'replay', priority: 1, chance: 0.7, cooldownMs: 25000, chain: true });
  };

  function stopReplayNarration(skipped: boolean): void {
    for (const id of REPLAY_LINES)
      engine.cancelLine?.(
        id,
        skipped
          ? { fadeMs: REPLAY_SKIP_FADE_MS }
          : { fadeMs: REPLAY_SKIP_FADE_MS, graceMs: REPLAY_END_GRACE_MS },
      );
  }

  // ── settings & pause ────────────────────────────────────────────────────────────────────────────────────
  disposers.push(
    effect(() => {
      engine.setVolumes(ui.settings.value.volumes);
    }),
    effect(() => {
      // The pause menu muffles the whole stadium; an instant replay (VHS tape) muffles it too, but the booth
      // stays clear: the announcer calls the tape.
      const menu = ui.paused.value;
      const tape = ui.replay.value !== null;
      const live = IN_STADIUM.has(screen);
      if (menu || !tape) engine.setPaused?.(menu && live);
      else engine.setPaused?.(live, { booth: false });
    }),
    // Instant replay: a whoosh with each broadcast wipe (into the tape, back to live), and the booth's call.
    effect(() => {
      const r = ui.replay.value;
      const stage = r?.stage ?? null;
      if (stage !== replayStage && (stage === 'in' || stage === 'out')) engine.sfx('ui_whoosh');
      if (stage === 'play' && replayStage !== 'play' && r) callReplay(r.clip.id, r.clip.title);
      // the tape is over (skipped, or wiping back to live): its narration must not run on over the next play
      // ("…he could go all the way… Extra point is GOOD!"). Whatever is still talking fades, whatever is queued goes.
      const rolling = (s: string | null): boolean => s === 'in' || s === 'play' || s === 'hold';
      // skipped mid-tape (in/play → out) vs the tape's own end (hold → out: the narration is nearly done)
      if (rolling(replayStage) && !rolling(stage)) stopReplayNarration(replayStage !== 'hold');
      else if (stage === null && replayStage !== null) stopReplayNarration(false); // back to live: nothing left over
      replayStage = stage;
    }),
  );
  // Football 101 (lessons run on the match screen). The lesson HUD plays the ✓ / ✗ sounds and the coach's lines
  // (TutorialOverlay); the bridge adds the stadium's part: a cheer + chime when a lesson is cleared, and the
  // graduation — the play's call and any boo are cut, then a fanfare, an air horn and a roar under "You made the
  // team!!" (a Game Day loss finishes the course too: a chime and applause under "Good game!"). Legacy views (no `phase`) still get a tick per rep and a chime per step.
  let tutStatus: string | null = null;
  let tutPhase: string | undefined;
  let tutProgress = 0;
  let tutKey = '';
  disposers.push(
    effect(() => {
      const t = ui.tutorial.value;
      if (!t || screen !== 'match') {
        tutStatus = null;
        tutPhase = undefined;
        tutProgress = 0;
        return;
      }
      const key = `${t.lessonIndex}:${t.stepIndex}`;
      if (t.status === 'complete' && tutStatus !== 'complete' && tutStatus !== null) {
        engine.hush?.(220);
        const lost = t.scrimmage?.result === 'loss' || t.scrimmage?.result === 'tie';
        if (lost) {
          // Game Day lost (or tied): the course is still done, but no touchdown fanfare under "Good game! Run it
          // back!" — a warm chime and the crowd's applause
          engine.sfx('conversion_good', { pan: 0, volume: 0.8 });
          engine.crowdReact('cheer', 0.5);
        } else {
          engine.sfx('td_fanfare', { pan: 0 });
          engine.sfx('air_horn', { pan: 0, volume: 0.5 });
          engine.crowdReact('roar', 0.8);
        }
      } else if (t.phase !== undefined) {
        if (t.phase === 'clear' && tutPhase !== 'clear' && tutPhase !== undefined) {
          engine.sfx('conversion_good', { pan: 0, volume: 0.75 });
          engine.crowdReact('cheer', 0.55);
        }
      } else if (t.status === 'success' && tutStatus === 'active') {
        engine.sfx('first_down', { pan: 0, volume: 0.8 });
        engine.crowdReact('cheer', 0.45);
      } else if (
        t.status === 'active' &&
        key === tutKey &&
        t.progress > tutProgress + 1e-6 &&
        t.progress < 1
      ) {
        engine.sfx('ui_tick', { pan: 0, volume: 0.7 });
      }
      tutStatus = t.status;
      tutPhase = t.phase;
      tutProgress = t.progress;
      tutKey = key;
    }),
  );

  if (engine.onUnlock) {
    disposers.push(
      engine.onUnlock(() => {
        if (!disposed && !titleShouted && (screen === 'title' || screen === 'main_menu')) {
          titleShouted = true;
          say('title', 2);
        }
      }),
    );
  }

  const resetMatchMemory = (): void => {
    seen.clear();
    seenOrder.length = 0;
    heat[0] = heat[1] = 0;
    heatingCalled[0] = heatingCalled[1] = false;
    heatingDone[0] = heatingDone[1] = false;
    fireDone[0] = fireDone[1] = false;
    pendingStreak = null;
    saidOnce.clear();
    lastTdYards = 0;
    held = null;
    deadBallLines.length = 0;
    replayedPlay = false;
    prevClock = -1;
    prevHalf = 0;
    thirtyUntil = -1;
    snapped = false;
    finished.clear();
    lastStateTick = -1;
    crowdSent = -1;
    prevHop[0] = prevHop[1] = 0;
    prevRun[0] = prevRun[1] = false;
  };

  /** Say the held streak call once its hold is over — unless the game ended meanwhile. */
  const flushStreak = (state: SimState): void => {
    const p = pendingStreak;
    if (!p || now() < p.at) return;
    pendingStreak = null;
    if (state.rules.phase === Phase.GAME_OVER || graduated()) return;
    tryLine({ id: p.id, priority: p.priority, cooldownMs: 20000, chain: true });
  };

  /**
   * The ball carrier is running at the goal from close in (live play, ball in hand, ≤ `HIGHLIGHT_HOLD_YARDS` to
   * the goal line he attacks, or ≤ `HIGHLIGHT_HOLD_LOOKAHEAD_S` away at his speed), or the offense's pass is in
   * the air there: a touchdown may come any moment.
   */
  const scoringRun = (s: SimState): boolean => {
    const r = s.rules;
    if (r.phase !== Phase.LIVE && r.phase !== Phase.RUSH_COUNT) return false;
    const b = s.ball;
    if (b.state === BallState.AIR) {
      if (b.thrownBy !== r.offense || b.fgAttempt || b.kick) return false;
      const dir = r.attackDir || 1;
      return yardsToGoal(b.x, dir) <= Math.max(HIGHLIGHT_HOLD_YARDS, b.vx * dir * HIGHLIGHT_HOLD_LOOKAHEAD_S);
    }
    if (b.state !== BallState.HELD || (b.holder !== 0 && b.holder !== 1)) return false;
    const dir = r.attackDir || 1;
    const carrierDir = b.holder === r.offense ? dir : -dir;
    const p = s.players[b.holder];
    const toward = p.vx * carrierDir;
    return (
      yardsToGoal(p.x, carrierDir) <= Math.max(HIGHLIGHT_HOLD_YARDS, toward * HIGHLIGHT_HOLD_LOOKAHEAD_S)
    );
  };

  /** A held highlight: stale after `HIGHLIGHT_HOLD_MAX_MS`; said once the scoring run is over without a score. */
  const flushHeld = (s: SimState): void => {
    const h = held;
    if (!h) return;
    if (now() - h.since > HIGHLIGHT_HOLD_MAX_MS || graduated() || s.rules.phase === Phase.GAME_OVER)
      held = null;
    else if (!scoringRun(s)) {
      held = null;
      speak(h.cue, now());
    }
  };

  /**
   * Say a dead-ball line (the special-ready callout) once the ball is dead and the booth has room: between plays —
   * never live, never over an instant replay (a scoring play's dead ball is the tape's: it waits for the try or
   * the next snap's set-up), into a free booth. Retried every frame until said; a cue whose cooldown has not run
   * out is dropped.
   */
  const flushDeadBall = (s: SimState): void => {
    const c = deadBallLines[0];
    if (!c || screen !== 'match') return;
    const r = s.rules;
    if (r.phase === Phase.GAME_OVER || graduated()) {
      deadBallLines.length = 0;
      return;
    }
    const deadBall =
      r.phase === Phase.CONVERSION ||
      (r.phase === Phase.DEAD && !replayedPlay) ||
      (r.phase === Phase.PRE_SNAP && r.phaseFrame <= DEAD_BALL_PRESNAP_FRAMES);
    if (!deadBall || ui.replay.peek() !== null) return;
    // only into a free booth: queued behind a call, it could still be dropped unsaid (a bigger call clears the
    // queue: "And that's the HALF!") and would be lost
    const booth = engine.stats?.();
    if (booth && (booth.speaking !== null || booth.queued > 0)) return;
    const t = now();
    if (!gate(c, t) || speak(c, t)) deadBallLines.shift();
  };

  /** A player used his special: his "…is all charged up" callout is stale. */
  const dropReadyLine = (s: SimState, actor: number): void => {
    const p = actor === 0 || actor === 1 ? s.players[actor] : null;
    const id = p ? READY_LINES[CHARACTER_IDS[p.character] ?? ''] : undefined;
    for (let i = deadBallLines.length - 1; i >= 0; i--)
      if (deadBallLines[i]!.id === id) deadBallLines.splice(i, 1);
  };

  const remember = (key: string): boolean => {
    if (seen.has(key)) return false;
    seen.add(key);
    seenOrder.push(key);
    if (seenOrder.length > DEDUPE_CAPACITY) {
      for (const k of seenOrder.splice(0, seenOrder.length - DEDUPE_CAPACITY)) seen.delete(k);
    }
    return true;
  };

  const setChant = (on: boolean): void => {
    if (on === chantSent) return;
    chantSent = on;
    engine.crowdChant?.(on);
  };

  /**
   * A replay tape's re-simulated events (in-match instant replay or the Results theater): the hits, catches,
   * whistles and fanfares under the stadium's "to tape" muffle, one stinger per frame and a softer crowd. No live
   * calls (the booth narrates the tape), no heat, and no de-duplication: the tape's events share their keys with
   * the live ones they replay.
   */
  function playTape(events: readonly SimEvent[], state: SimState, cameraX: number): void {
    clearBatch(batch);
    for (const e of events) cuesFor(e, state, cameraX, batch);
    let best = -1;
    for (const c of batch.sfx) best = Math.max(best, STINGER_RANK[c.id] ?? -1);
    for (const c of batch.sfx) {
      const rank = STINGER_RANK[c.id];
      if (rank !== undefined && rank < best) continue;
      engine.sfx(c.id, { volume: c.volume, pitch: c.pitch, pan: c.pan });
    }
    let react: { kind: CrowdReaction; strength: number } | null = null;
    for (const c of batch.crowd) if (!react || c.strength > react.strength) react = c;
    if (react) engine.crowdReact(react.kind, react.strength * 0.7);
  }

  function closeTheater(): void {
    if (!theaterOn) return;
    theaterOn = false;
    engine.setMusicLevel?.(1, 800);
    engine.setPaused?.(false);
  }

  const bridge: AudioBridge = {
    onEvents(events: readonly SimEvent[], state: SimState, cameraX: number): void {
      if (disposed || !IN_STADIUM.has(screen)) return;
      // a new timeline (new match, practice reset): forget old keys, ticks restart
      if (state.tick < lastStateTick) resetMatchMemory();
      lastStateTick = state.tick;
      if (events.length === 0) return;
      clearBatch(batch);
      const kind = mode();
      let gameOver = false;
      for (const e of events) {
        if (!remember(eventKey(e))) continue;
        // "Let's play some FOOTBALL!" opens a MATCH (never a drill or a lesson): the first drive (Drives) or the
        // opening kickoff (Classic)
        if (
          kind === 'match' &&
          !snapped &&
          ((e.type === 'DRIVE_START' && !e.overtime && e.driveNumber <= 1) ||
            (e.type === 'KICKOFF_START' && e.half <= 1))
        )
          // chained: it follows the Versus intro ("THE ROOKIE! … BIG TOM!") still running over the curtain
          batch.lines.push({ id: 'kickoff', priority: 2, cooldownMs: 0, once: true, chain: true });
        if (e.type === 'SNAP' || e.type === 'KICK') {
          // play is on: an opener still waiting behind the intro would land mid-play — drop it
          if (!snapped) engine.cancelLine?.('kickoff');
          snapped = true;
          // "Fourth down… big play here!" is about the snap: once the ball is live it is stale
          engine.cancelLine?.('fourth_down');
        }
        // streak calls are counted per half (Classic) / regulation and overtime (Drives)
        if ((e.type === 'HALF_END' && e.half === 1) || e.type === 'OVERTIME_START') {
          heatingDone[0] = heatingDone[1] = false;
          fireDone[0] = fireDone[1] = false;
        }
        if (
          e.type === 'SNAP' ||
          e.type === 'OFFSIDE' ||
          e.type === 'DRIVE_START' ||
          e.type === 'KICKOFF_START' ||
          e.type === 'CONVERSION_START'
        )
          clearFinishedCaptions();
        if (e.type === 'GAME_OVER') gameOver = true;
        if (e.type === 'PLAY_END') {
          replayedPlay = REPLAYED_RESULTS.has(e.result);
          if (e.result === PlayResultKind.TOUCHDOWN) lastTdYards = Math.abs(e.yards);
        }
        if (e.type === 'SPECIAL_ACTIVATE') dropReadyLine(state, e.actor);
        if (e.type === 'DOWN_START') setChant(e.down >= 3 && screen === 'match');
        if (e.type === 'SNAP' || e.type === 'OFFSIDE') setChant(false);
        const before = batch.sfx.length;
        cuesFor(e, state, cameraX, batch);
        if (state.tick - e.tick > STALE_TICKS) {
          // late delivery: keep only the important sounds
          for (let i = batch.sfx.length - 1; i >= before; i--)
            if (!batch.sfx[i]!.urgent) batch.sfx.splice(i, 1);
        }
      }
      // SFX: every cue, except that only the best result stinger sounds this frame
      let best = -1;
      for (const c of batch.sfx) best = Math.max(best, STINGER_RANK[c.id] ?? -1);
      const played = new Set<string>();
      for (const c of batch.sfx) {
        const rank = STINGER_RANK[c.id];
        if (rank !== undefined && rank < best) continue;
        if (rank !== undefined && played.has(c.id)) continue;
        played.add(c.id);
        engine.sfx(c.id, { volume: c.volume, pitch: c.pitch, pan: c.pan });
      }
      if (graduated()) {
        // the Football 101 graduation owns the moment: no call, no boo over "You made the team!!"
        batch.lines.length = 0;
        batch.crowd.length = 0;
      } else if (kind === 'tutorial' && !gameDay()) {
        // a lesson: the coach talks; the stadium only calls the big moments (TOUCHDOWN, SACKED, IT'S GOOD…)
        for (let i = batch.lines.length - 1; i >= 0; i--)
          if (batch.lines[i]!.priority < LESSON_MIN_PRIORITY) batch.lines.splice(i, 1);
        batch.heat.length = 0; // drills are no streak
      }
      // dead-ball lines wait for the whistle (flushDeadBall)
      for (let i = batch.lines.length - 1; i >= 0; i--) {
        const c = batch.lines[i]!;
        if (!c.deadBall) continue;
        batch.lines.splice(i, 1);
        if (!deadBallLines.some((d) => d.id === c.id)) deadBallLines.push(c);
      }
      if (gameOver) {
        // the final whistle: a streak call still waiting would push "THAT'S THE BALLGAME!!" past its moment
        engine.cancelLine?.('on_fire');
        engine.cancelLine?.('heating_up');
        pendingStreak = null;
        held = null;
        deadBallLines.length = 0;
      }
      // heat → "HE'S ON FIRE!!"
      for (const p of batch.coolOpponentOf)
        if (p === 0 || p === 1) {
          heat[1 - p] = 0;
          heatingCalled[1 - p] = false;
        }
      let onFire = false;
      let heatingUp = false;
      const heat0 = heat[0]!;
      const heat1 = heat[1]!;
      for (const h of batch.heat) {
        if (h.player !== 0 && h.player !== 1) continue;
        const before = h.player === 0 ? heat0 : heat1;
        heat[h.player]! += h.amount;
        if (heat[h.player]! >= ON_FIRE_HEAT) {
          heat[h.player] = 0;
          heatingCalled[h.player] = false;
          // once per player per period: a streak call that comes every few minutes means nothing
          if (!fireDone[h.player]) {
            fireDone[h.player] = true;
            onFire = true;
          }
        } else if (heat[h.player]! >= HEATING_UP_HEAT && before > 0 && !heatingCalled[h.player]) {
          // a streak, not one big play
          heatingCalled[h.player] = true;
          if (!heatingDone[h.player]) {
            heatingDone[h.player] = true;
            heatingUp = true;
          }
        }
      }
      // announcer: the best candidate of the frame, then the streak call queued right behind it
      // (a streak call follows the frame's line at its priority, so it queues instead of cutting it off)
      let top = 2;
      if (held) {
        // a highlight held for a scoring run: a bigger call this frame (TOUCHDOWN!!, PICKED OFF!) replaces it; the
        // run over without one (tackled at the 2, a throw), it competes with the frame's calls now
        const h = held;
        if (batch.lines.some((c) => c.priority >= 3)) held = null;
        else if (!scoringRun(state)) {
          held = null;
          batch.lines.push({ ...h.cue, chance: undefined });
        }
      }
      if (batch.lines.length) {
        batch.lines.sort((a, b) => b.priority - a.priority);
        let said: LineCue | null = null;
        for (const c of batch.lines) {
          if (HOLDABLE.has(c.id) && scoringRun(state) && !gameOver) {
            // the carrier is a few strides from the goal: hold the call (and anything smaller this frame)
            if (gate(c, now())) held = { cue: { ...c, chance: undefined }, since: now() };
            break;
          }
          if (tryLine(c)) {
            top = c.priority;
            said = c;
            break;
          }
        }
        // a call that must be heard even when a bigger one won the frame follows it ("SACKED!! Buried him!" →
        // "Stopped short! Turnover on downs!": the change of possession is news)
        if (said)
          for (const c of batch.lines)
            if (c.follow && c !== said) tryLine({ ...c, priority: top, chain: true, follow: false });
      }
      // held a moment (a walk-off score's final whistle comes a few ticks later and drops it), then chained: it
      // waits for the frame's line however long it runs
      if (gameOver || graduated()) {
        /* the game is over: the end-of-game call follows instead */
      } else if (onFire) pendingStreak = { id: 'on_fire', priority: top, at: now() + STREAK_HOLD_MS };
      else if (heatingUp) pendingStreak = { id: 'heating_up', priority: top, at: now() + STREAK_HOLD_MS };
      flushStreak(state);
      flushDeadBall(state);
      // crowd: the strongest reaction of the frame
      let react: { kind: CrowdReaction; strength: number } | null = null;
      for (const c of batch.crowd) if (!react || c.strength > react.strength) react = c;
      if (react)
        engine.crowdReact(react.kind, kind === 'match' || gameDay() ? react.strength : react.strength * 0.5);
    },

    update(state: SimState): void {
      if (disposed || !IN_STADIUM.has(screen) || screen === 'versus') return;
      if (state.tick < lastStateTick) resetMatchMemory();
      flushStreak(state);
      flushHeld(state);
      flushDeadBall(state);
      const level = crowdLevel(state, gameDay() && mode() === 'tutorial' ? 'match' : mode());
      if (Math.abs(level - crowdSent) > 0.01) {
        crowdSent = level;
        engine.crowd(level);
      }
      if (state.rules.phase !== Phase.PRE_SNAP && chantSent) setChant(false);
      // Classic: the clock crosses :30 in a half
      const r = state.rules;
      if (r.format === MatchFormat.CLASSIC && !r.overtime && mode() === 'match') {
        if (
          r.half === prevHalf &&
          prevClock > CLOCK_WARNING_FRAMES &&
          r.gameClock <= CLOCK_WARNING_FRAMES &&
          r.gameClock > 0
        )
          thirtyUntil = now() + CLOCK_WARNING_RETRY_MS;
        prevClock = r.gameClock;
        prevHalf = r.half;
        // the warning waits its turn (a call may be running at :30; it never cuts one): retried until it is said
        if (thirtyUntil >= 0) {
          if (now() > thirtyUntil || r.gameClock <= 0 || r.phase === Phase.GAME_OVER) thirtyUntil = -1;
          // the second half's warning is the game's: "Thirty seconds left in the GAME… it's now or NEVER!"
          else if (say(r.half >= 2 ? 'thirty_seconds_game' : 'thirty_seconds', 1) && !preciseCaptions)
            thirtyUntil = -1;
        }
      }
      // hop-run footsteps, synced to the sim's hop phase (wrap = the hop lands)
      if (state.hitStop > 0) return;
      for (let i = 0; i < 2; i++) {
        const p = state.players[i as 0 | 1];
        const running = p.state === PlayerState.RUN && p.grounded;
        if (running && prevRun[i] && p.hopPhase + 0.5 < prevHop[i]!) {
          engine.sfx('hop', { pan: panFor(p.x, state.camera.x), volume: 0.8 });
        }
        prevRun[i] = running;
        prevHop[i] = p.hopPhase;
      }
    },

    onScreen(next: ScreenId): void {
      if (disposed) return;
      const prev = screen;
      screen = next;
      // a match / lesson / drill ticks on the match screen: background audio work must not take its frames
      engine.setBusy?.(next === 'match');
      const music = musicFor(next, musicSent);
      if (music !== 'keep') {
        musicSent = music;
        engine.music(music, next === 'title' ? 600 : 1400);
      }

      if (!IN_STADIUM.has(next)) {
        setChant(false);
        if (next !== 'results') {
          if (crowdSent !== 0) engine.crowd(0);
          crowdSent = 0;
          // quitting mid-play / leaving the results: no roar, bark or stinger may follow us into the menus
          if (isLive(prev)) engine.hush?.(LEAVE_FADE_MS);
        }
        engine.setPaused?.(false);
      }
      // a lesson or drill handing off to a real game (Football 101's PLAY A GAME): the coach's line still talking
      // goes (quickly: the splash's names are next) and the new match starts from a clean slate
      const handoff = next === 'versus' && prev === 'match';
      if (handoff) engine.hush?.(HANDOFF_FADE_MS);
      if ((IN_STADIUM.has(next) && !IN_STADIUM.has(prev)) || handoff) resetMatchMemory();
      if (prev === 'results' && next !== 'results') closeTheater();
      // tiered loading: everything still deferred is wanted once a match, a lesson or a drill is near
      if (next === 'versus' || next === 'match' || next === 'tutorial' || next === 'practice')
        void engine.prefetch?.();

      switch (next) {
        case 'title':
          if (engine.unlocked && !titleShouted) {
            titleShouted = true;
            say('title', 2);
          }
          break;
        case 'character_select':
          if (prev !== 'character_select')
            tryLine({ id: 'choose_your_head', priority: 1, cooldownMs: 20000 });
          break;
        case 'versus': {
          // the stadium fades in under "THE ROOKIE! … VERSUS! … BIG TOM!"
          engine.crowd(0.3);
          crowdSent = 0.3;
          // the splash's match (ui.matchSetup is only written when Versus starts it)
          const heads = (pendingMatch.peek()?.setup ?? ui.matchSetup.peek())?.config.characters;
          const a = heads ? characterLine(CHARACTER_IDS.indexOf(heads[0])) : null;
          const b = heads ? characterLine(CHARACTER_IDS.indexOf(heads[1])) : null;
          // priority 2: "Choose your head!" may still be talking when a quick pick lands here. The two names fit the
          // 3.4 s splash ("THE ROOKIE! … BIIIG TEX!", the big VS on screen says the rest); "VERSUS!" alone when
          // the heads are unknown.
          if (a && b) {
            say(a, 2);
            say(b, 2, true);
          } else say('versus', 2);
          break;
        }
        case 'results':
          engine.crowd(RESULTS_CROWD);
          crowdSent = RESULTS_CROWD;
          // chained: "THAT'S THE BALLGAME!!" (queued the same frame) finishes first
          tryLine({ id: 'winner', priority: 2, cooldownMs: 0, chain: true });
          break;
        default:
          break;
      }
    },

    onTheater(tape: TheaterTape | null): void {
      if (disposed) return;
      if (!tape) {
        // closing the theater: its narration does not run on over the results
        if (theaterOn) stopReplayNarration(true);
        closeTheater();
        return;
      }
      if (screen !== 'results') return;
      if (!theaterOn) {
        theaterOn = true;
        // to tape: the outro steps back, the tape's stadium is muffled, the booth stays clear
        engine.setMusicLevel?.(THEATER_MUSIC, 500);
        engine.setPaused?.(true, { booth: false });
      }
      engine.sfx('ui_whoosh');
      const long = /TOUCHDOWN|PICK SIX/i.test(tape.title) && (tape.yards ?? 0) >= LONG_TD_YARDS;
      // the reel's long scores get the narration at most every 20 s (its takes rotating), the others the lead-in
      if (
        long &&
        tryLine({ id: 'long_td_reel', priority: 2, cooldownMs: LONG_TD_REEL_COOLDOWN_MS, chain: true })
      )
        return;
      tryLine({ id: 'replay', priority: 2, cooldownMs: 6000, chain: true });
    },

    onTheaterEvents(events: readonly SimEvent[], state: SimState, cameraX: number): void {
      if (disposed || !theaterOn || screen !== 'results' || events.length === 0) return;
      playTape(events, state, cameraX);
    },

    onReplayEvents(events: readonly SimEvent[], state: SimState, cameraX: number): void {
      if (disposed || screen !== 'match' || events.length === 0) return;
      const stage = ui.replay.peek()?.stage;
      if (stage !== 'play' && stage !== 'hold') return;
      playTape(events, state, cameraX);
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      closeTheater();
      for (const d of disposers.splice(0)) d();
      setChant(false);
      if (isLive(screen)) engine.hush?.(LEAVE_FADE_MS);
      engine.crowd(0);
      engine.music(null, 400);
    },
  };
  return bridge;
};
